// EditMatchModal.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./css/GamesStatsPage.css"; // Ensure this path is correct

const API_URL =
  "https://vhlyllb4ea.execute-api.us-east-2.amazonaws.com/prod/matchStatsAPI";

/**
 * EditMatchModal:
 *  - isOpen: bool
 *  - onClose: function to close
 *  - userId: string
 *  - teamId: string
 *  - matchId: string
 *  - refreshMatches: function to refresh the main list on save
 */
const EditMatchModal = ({
  isOpen,
  onClose,
  userId,
  teamId,
  matchId,
  refreshMatches,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Full match object from DB
  const [matchData, setMatchData] = useState({
    myTeam: "",
    opposition: "",
    homeAway: "Home",
    match_date: "",
    teamScores: { myTeamScore: 0, oppositionScore: 0 },
    playerStats: [],
  });

  // Accordion States
  const [isMatchOutcomeOpen, setIsMatchOutcomeOpen] = useState(false);
  const [isPlayerStatsOpen, setIsPlayerStatsOpen] = useState(false);
  const [expandedPlayers, setExpandedPlayers] = useState({}); // Track expanded state for each player

  useEffect(() => {
    if (isOpen) {
      fetchSingleMatch();
    } else {
      // Reset state when modal is closed
      setMatchData({
        myTeam: "",
        opposition: "",
        homeAway: "Home",
        match_date: "",
        teamScores: { myTeamScore: 0, oppositionScore: 0 },
        playerStats: [],
      });
      setExpandedPlayers({});
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchSingleMatch = async () => {
    setLoading(true);
    setError("");
    try {
      // Define a new operation "get_single_match" that returns
      // the entire match record: homeAway, match_date, myTeam, opposition,
      // teamScores, playerStats, etc.
      const requestBody = {
        operationType: "get_single_match",
        data: {
          team_id: teamId,
          match_id: matchId,
        },
      };
      console.log(
        "[DEBUG] Fetching single match with requestBody:",
        requestBody
      );

      const response = await axios.post(API_URL, requestBody, {
        headers: { "Content-Type": "application/json" },
      });
      console.log("[DEBUG] Response from get_single_match:", response);

      if (response.status === 200) {
        // Suppose it returns { match: {...} }
        const { match } = response.data;
        if (match) {
          setMatchData({
            myTeam: match.myTeam || "",
            opposition: match.opposition || "",
            homeAway: match.homeAway || "Home",
            match_date: match.match_date || "",
            teamScores: match.teamScores || {
              myTeamScore: 0,
              oppositionScore: 0,
            },
            playerStats: match.playerStats || [],
          });
        }
      } else {
        setError(
          "Failed to fetch single match. " + (response.data.error || "")
        );
      }
    } catch (err) {
      console.error("Error fetching single match:", err);
      setError("Unable to fetch match. Check console.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update local matchData => handle match-level fields
   */
  const handleMatchFieldChange = (field, value) => {
    setMatchData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Update local matchData => handle teamScores
   */
  const handleTeamScoreChange = (field, value) => {
    const numericValue = parseInt(value, 10);
    setMatchData((prev) => ({
      ...prev,
      teamScores: {
        ...prev.teamScores,
        [field]: isNaN(numericValue) ? 0 : numericValue,
      },
    }));
  };

  /**
   * Update local matchData => handle playerStats
   * Each playerStats entry has {player_id, player_name, position, goals, etc.}
   */
  const handlePlayerStatChange = (index, field, value) => {
    let updatedValue = value;
    // Parse numerical fields
    if (
      [
        "goals",
        "assists",
        "shots_on_target",
        "shots_off_target",
        "defensive_actions",
        "saves",
      ].includes(field)
    ) {
      const parsed = parseInt(value, 10);
      updatedValue = isNaN(parsed) ? 0 : parsed;
    }
    setMatchData((prev) => {
      const updatedStats = [...prev.playerStats];
      updatedStats[index] = {
        ...updatedStats[index],
        [field]: updatedValue,
      };
      return { ...prev, playerStats: updatedStats };
    });
  };

  /**
   * Save changes => "update_match" passing the entire record
   */
  const handleSave = async () => {
    setLoading(true);
    setError("");

    // Construct the entire match object to send
    const requestBody = {
      operationType: "update_match",
      data: {
        team_id: teamId,
        match_id: matchId,
        myTeam: matchData.myTeam, // Assuming this should not change
        opposition: matchData.opposition,
        homeAway: matchData.homeAway,
        match_date: matchData.match_date,
        myTeamScore: matchData.teamScores.myTeamScore,
        oppositionScore: matchData.teamScores.oppositionScore,
        playerStats: matchData.playerStats.map((p) => ({
          player_id: p.player_id,
          player_name: p.player_name,
          position: p.position,
          goals: p.goals,
          assists: p.assists,
          shots_on_target: p.shots_on_target,
          shots_off_target: p.shots_off_target,
          defensive_actions: p.defensive_actions,
          saves: p.saves,
          status: p.status,
        })),
      },
    };

    console.log("[DEBUG] Saving match with requestBody:", requestBody);

    try {
      const response = await axios.post(API_URL, requestBody, {
        headers: { "Content-Type": "application/json" },
      });
      console.log("[DEBUG] Response from update_match:", response);

      if (response.status === 200) {
        onClose();
        refreshMatches(); // refresh the main list
      } else {
        setError("Failed to update match. " + (response.data.error || ""));
      }
    } catch (err) {
      console.error("Error saving match:", err);
      setError("Unable to save match. Check console.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle Accordion Sections
   */
  const toggleMatchOutcome = () => {
    setIsMatchOutcomeOpen((prev) => !prev);
  };

  const togglePlayerStats = () => {
    setIsPlayerStatsOpen((prev) => !prev);
  };

  /**
   * Toggle individual player accordion
   */
  const togglePlayerAccordion = (playerId) => {
    setExpandedPlayers((prev) => ({
      ...prev,
      [playerId]: !prev[playerId],
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="gs-edit-match-modal-overlay">
      <div className="gs-edit-match-modal-content">
        {/* Close Button */}
        <span
          className="gs-edit-match-modal-close-button"
          onClick={onClose}
          aria-label="Close Modal"
        >
          &times;
        </span>

        <h2 id="editMatchModalTitle">Edit Match</h2>
        {error && <div className="gs-edit-match-error-message">{error}</div>}
        {loading && <div className="gs-edit-match-loading">Loading...</div>}

        {/* Match Outcome Accordion */}
        <div className="gs-accordion">
          <div
            className="gs-accordion-header"
            onClick={toggleMatchOutcome}
            aria-expanded={isMatchOutcomeOpen}
            aria-controls="matchOutcomeContent"
          >
            <span>Match Outcome</span>
            <span>{isMatchOutcomeOpen ? "-" : "+"}</span>
          </div>
          <div
            id="matchOutcomeContent"
            className={`gs-accordion-content ${
              isMatchOutcomeOpen ? "active" : ""
            }`}
          >
            {/* MATCH-LEVEL FIELDS */}
            <div className="gs-form-group">
              <label>My Team:</label>
              <span className="gs-readonly-field">{matchData.myTeam}</span>
            </div>
            <div className="gs-form-group">
              <label>Opposition:</label>
              <input
                type="text"
                value={matchData.opposition}
                onChange={(e) =>
                  handleMatchFieldChange("opposition", e.target.value)
                }
              />
            </div>
            <div className="gs-form-group">
              <label>Home/Away:</label>
              <select
                value={matchData.homeAway}
                onChange={(e) =>
                  handleMatchFieldChange("homeAway", e.target.value)
                }
              >
                <option value="Home">Home</option>
                <option value="Away">Away</option>
              </select>
            </div>
            <div className="gs-form-group">
              <label>Match Date:</label>
              <input
                type="datetime-local"
                value={matchData.match_date}
                onChange={(e) =>
                  handleMatchFieldChange("match_date", e.target.value)
                }
              />
            </div>
            <div className="gs-form-group">
              <label>My Team Score:</label>
              <input
                style={{ width: 60 }}
                type="number"
                min="0"
                value={matchData.teamScores.myTeamScore}
                onChange={(e) =>
                  handleTeamScoreChange("myTeamScore", e.target.value)
                }
              />
              <label style={{ marginLeft: 20 }}>Opposition Score:</label>
              <input
                style={{ width: 60 }}
                type="number"
                min="0"
                value={matchData.teamScores.oppositionScore}
                onChange={(e) =>
                  handleTeamScoreChange("oppositionScore", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        {/* Player Stats Accordion */}
        <div className="gs-accordion">
          <div
            className="gs-accordion-header"
            onClick={togglePlayerStats}
            aria-expanded={isPlayerStatsOpen}
            aria-controls="playerStatsContent"
          >
            <span>Player Stats</span>
            <span>{isPlayerStatsOpen ? "-" : "+"}</span>
          </div>
          <div
            id="playerStatsContent"
            className={`gs-accordion-content ${
              isPlayerStatsOpen ? "active" : ""
            }`}
          >
            {/* PLAYER STATS SECTION */}
            {matchData.playerStats.length === 0 && <p>No players found.</p>}
            {matchData.playerStats.map((player, index) => (
              <div key={player.player_id} className="gs-edit-match-player-stat">
                {/* Player Name Header */}
                <div
                  className="gs-player-name-header"
                  onClick={() => togglePlayerAccordion(player.player_id)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: "#f0f0f0",
                    padding: "8px",
                    borderRadius: "4px",
                    marginBottom: "4px",
                  }}
                  aria-expanded={!!expandedPlayers[player.player_id]}
                >
                  <span>{player.player_name}</span>
                  <span>{expandedPlayers[player.player_id] ? "-" : "+"}</span>
                </div>

                {/* Player Details Accordion */}
                {expandedPlayers[player.player_id] && (
                  <div
                    className="gs-player-details"
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      marginBottom: "8px",
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <label>Position:</label>
                      <input
                        type="text"
                        value={player.position}
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "position",
                            e.target.value
                          )
                        }
                        style={{ width: 80, marginLeft: 5 }}
                      />
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <label>Goals:</label>
                      <input
                        style={{ width: 50, marginLeft: 5 }}
                        type="number"
                        min="0"
                        value={player.goals}
                        onChange={(e) =>
                          handlePlayerStatChange(index, "goals", e.target.value)
                        }
                      />
                      <label style={{ marginLeft: 10 }}>Assists:</label>
                      <input
                        style={{ width: 50, marginLeft: 5 }}
                        type="number"
                        min="0"
                        value={player.assists}
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "assists",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <label>Shots On:</label>
                      <input
                        style={{ width: 50, marginLeft: 5 }}
                        type="number"
                        min="0"
                        value={player.shots_on_target}
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "shots_on_target",
                            e.target.value
                          )
                        }
                      />
                      <label style={{ marginLeft: 10 }}>Shots Off:</label>
                      <input
                        style={{ width: 50, marginLeft: 5 }}
                        type="number"
                        min="0"
                        value={player.shots_off_target}
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "shots_off_target",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <label>Defensive Actions:</label>
                      <input
                        style={{ width: 50, marginLeft: 5 }}
                        type="number"
                        min="0"
                        value={player.defensive_actions}
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "defensive_actions",
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <label>Saves:</label>
                      <input
                        style={{ width: 50, marginLeft: 5 }}
                        type="number"
                        min="0"
                        value={player.saves}
                        onChange={(e) =>
                          handlePlayerStatChange(index, "saves", e.target.value)
                        }
                      />
                      {/* Status Field (Optional) */}
                      <label style={{ marginLeft: 10 }}>Status:</label>
                      <select
                        value={player.status}
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "status",
                            e.target.value
                          )
                        }
                        style={{ marginLeft: 5 }}
                      >
                        <option value="playing">Playing</option>
                        <option value="sub">Sub</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Save / Cancel Buttons */}
        <div className="gs-add-match-modal-buttons">
          <button
            className="gs-save-button gs-btn gs-save-btn"
            onClick={handleSave}
            disabled={loading}
          >
            Save Changes
          </button>
          <button
            className="gs-cancel-button gs-btn gs-cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditMatchModal;
